import { CEC_STRUCTURE_TAB, CEC_STRUCTURE_TAB_NON_ADMIN } from '@/helper/featureFlags/constants';
import { hideCecFeature } from '@/lib/constants';
import { BrowserStorageService } from '@/services/browserStorage.service';
import featureFlagService from '@/services/feature-flag.service';
import { Button, Color } from '@usitsdasdesign/dds-react';
import { getPermsissionToAddFirmInitiative } from 'app/firm-initiatives/page';
import Icon from 'atoms/Icon';
import { getPathsForDelegate, MENU_ITEMS } from 'lib/auth';
import { signOut } from 'next-auth/react';
import {
    useEffect, useMemo, useRef, useState
} from 'react';
import { getDelegates } from 'services/delegate.service';
import { getSignedUrl } from 'services/storage.service';
import useGlobalToastStore from 'store/globalToastStore';
import useClickOutside from '../useClickOutSide';

const getInitials = function (name) {
    const parts = name.split(' ');
    let initials = '';
    for (let i = 0; i < parts.length; i++) {
        if (parts[i].length > 0 && parts[i] !== '') {
            initials += parts[i][0];
        }
    }
    return initials.toUpperCase();
};

async function openFile(file = 'faq', type) {
    if (file == 'faq') {
        const res = await getSignedUrl(`STATIC-UPLOADS/${type}-faq.pdf`);
        if (res.entity) {
            window.open(res.entity);
        }
    } else {
        const res = await getSignedUrl(`STATIC-UPLOADS/${type}-user-guide.pdf`);
        if (res.entity) {
            window.open(res.entity);
        }
    }
}

export const RenderSubMenu = ({ menus, label = 'Menu' }) => {
    const [show, setShow] = useState(false);
    const ref = useRef();
    useClickOutside(ref, () => setShow(false));
    return (
        <div ref={ref} className='sub-menu'>
            <div
                onClick={(e) => {
                    setShow(!show);
                    e.stopPropagation();
                }}
                className='flex items-center gap-2'
            >
                {label}
                <Icon
                    icon={show ? 'chevron_up__l__stroke' : 'chevron_down__l__stroke'}
                    color='white'
                    size='w-4 h-4'
                    className='transition-all'
                />
            </div>
            {
                show && (
                    <div className='sub-menu-inner bg-black absolute mt-4 left-0 w-full md:w-fit py-2 min-w-[14rem]'>
                        {
                            menus.map((menu, i) => (
                                <Button
                                    key={i}
                                    label={menu.title}
                                    color='dark'
                                    onClick={(e) => {
                                        menu.onClick();
                                        setShow(false);
                                        if (e.target.classList.contains('sub-menu-item') == false) {
                                            e.stopPropagation();
                                        }
                                    }}
                                    className='!w-full sub-menu-item'
                                />
                            ))
                        }
                    </div>
                )
            }
        </div>
    );
};

export const getHelpMenuOptions = ({ isActive, setToastData, type = 'employee', showFieldGlass = false, disableForTpcrRm = false, isshowfaq = false, showOnlyCecGuide = false }) => {
    if (showOnlyCecGuide) {
        return [
            {
                title: 'CEC User Guide',
                isActive: isActive('cec-user-guide'),
                onClick: () => window.open('/help/cec-user-guide', '_blank')
            }
        ];
    }

    const baseOptions = [
        ...(disableForTpcrRm && !isshowfaq ? [
            {
                title: 'FAQs',
                isActive: isActive('faqs'),
                onClick: () => openFile('faq', type)
            },
            {
                title: 'User Guide',
                isActive: isActive('user-guide'),
                onClick: () => openFile('userGuide', type)
            }
        ] : []),
        {
            title: 'Contact us',
            isActive: isActive('contact-us'),
            onClick: () => {
                setToastData({
                    show: true,
                    type: 'infoBlack',
                    closeLabel: 'OK',
                    message: 'Please reach out to "inrmteam@deloitte.com" for queries or feedback and we will get back to you at the earliest'
                });
            }
        }
    ];

    const fieldglassOptions = [
        {
            title: 'Fieldglass(Sub-Con) User Guide',
            isActive: isActive('fieldglass-user-guide'),
            onClick: () => window.open('/help/fieldglass-user-guide', '_blank')
        },
        {
            title: 'CEC User Guide',
            isActive: isActive('cec-user-guide'),
            onClick: () => window.open('/help/cec-user-guide', '_blank')
        },
        {
            title: 'Fieldglass(Sub-Con) Videos',
            isActive: isActive('fieldglass-videos'),
            onClick: () => window.open('/help/fieldglass-videos', '_blank')
        }
    ];

    return showFieldGlass ? [...baseOptions, ...fieldglassOptions] : baseOptions;
};


export const getOpportunityMenuOptions = ({ isActive, setToastData, type = 'employee', router }) => [
    {
        title: 'IJP (Internal Job Posting)',
        isActive: isActive('job-posting'),
        onClick: () => type === 'manager' ? router.push('/job-posting?manager=true') : router.push('/job-posting')
    },
    {
        title: 'Internal Gig',
        isActive: isActive('internal-gig-employee'),
        onClick: () => type === 'manager' ? router.push('/internal-gig-employee?manager=true') : router.push('/internal-gig-employee')
    },
    {
        title: 'Global Mobility',
        onClick: () => window.open('https://talentsourcing.deloitteresources.com/maps/', '_blank')
    },
    {
        title: 'Firm Initiatives',
        onClick: () => type === 'employee' ? router.push('/firm-initiatives-employee') : router.push('/firm-initiatives-employee?manager=true'),
    }
];

const HeaderArguments = (router, pathname, session, setShowEdit) => {
    const isActive = (route) => pathname.includes(route) && route !== '/';
    const isAdmin = session?.user?.role?.includes('admin');
    const isDelegate = useMemo(() => session?.isDelegate, [session]);
    const impersonatingUserId = useMemo(() => session?.impersonatingUserId, [session]);
    const [delegateTypes, setDelegateType] = useState([]);
    const { setToastData } = useGlobalToastStore();
    const fetchDelegations = async () => {
        const res = await getDelegates({ loading: false });
        if (res.status) {
            const impersonatingDelegate = res?.entity?.find((delegate) => delegate?.delegateId === impersonatingUserId);
            setDelegateType([session.delegateType]);
        }
    };

    const hasAccessToFirmInitiative = getPermsissionToAddFirmInitiative(session?.user) || session?.user?.isSpecificFirmInitiativeOwner;

    useEffect(() => {
        if (!session) return;
        fetchDelegations();
    }, [impersonatingUserId, isDelegate]);

    const delegateRoles = useMemo(() => [session?.user?.gradeLevel], [session]);
    const delegatePaths = useMemo(() => getPathsForDelegate({ delegateRoles, delegateTypes }), [delegateTypes, delegateRoles]);
    const isRM = session?.user?.role?.includes('resource_manager');
    const isRMLead = session?.user?.role?.includes('resource_manager_lead');
    const isTpcrRm = session?.user?.role?.includes('tpcr_rm');
    const isTpcrRmLead = session?.user?.role?.includes('tpcr_rm_lead');
    const isGuestRm = session?.user?.role?.includes('guest_rm')
    const isTalentAdvisor = session?.user?.role?.includes('talent_advisor');
    const isBusinessTeam = session?.user?.role?.includes('business_team');
    const isRecuritor = session?.user?.role?.includes('recruiter');
    const isSubRecuritor = session?.user?.role?.includes('sub_recruiter');
    const loginOnBehalf = session?.user?.loginOnBehalf;
    const isAdminOrRm = isAdmin || isRM || isRMLead;
    const isPartner = session?.user?.gradeLevel === 'partner';
    const isManager = session?.user?.gradeLevel === 'manager';
    const isAuditUser = (session?.user?.function === 'Audit & Assurance' && session?.user?.serviceArea === 'Audit') || session?.user?.isAuditRM || session?.user?.grade === 'DM';
    const accessType = session?.user?.accessType;
    const isSuperAdmin = session?.user?.role?.includes('super_admin');
    const isAudit = session?.user?.function === 'Audit & Assurance' && session?.user?.serviceArea === 'Audit'
    const isAuditMAndAbove = (((isRM || isPartner || isManager) && (session?.user?.function === 'Audit & Assurance' && session?.user?.serviceArea === 'Audit')) || isAdmin || isSuperAdmin);
    const isAdminOrPartnerOrManager = isPartner || isManager || isAdminOrRm || isSuperAdmin;
    const isAuditManagerOrAbove = isAdminOrPartnerOrManager && (session?.user?.function === 'Audit & Assurance' && session?.user?.serviceArea === 'Audit') || session?.user?.isAuditRM;
    const isAdminOrRmV1 = isAdmin || isRM || isGuestRm || isRMLead;
    const isGuestRmWithReviewTab = isGuestRm && (accessType === 'review_tab' || accessType === 'all')
    const hasRmAccess = isRM || isRMLead || isGuestRmWithReviewTab;
    const isAdminOrRmV2 = isAdmin || isRM;
    const hasStockCashReportAccess = (isAdmin || session?.user?.isAuditRM) || (isAudit && (isRM || isRMLead || isGuestRm || isBusinessTeam || isManager || isPartner));
    const showTimeSheet = session?.user?.timesheetIndicator == 'YES' && !isTpcrRm && !isTpcrRmLead;
    const propelRm = session?.user?.isSpecialProject && (isRM || isRMLead || (isGuestRm && (accessType === 'all' || accessType === 'reports')));
    const hasNoRole = !session?.user?.role?.length;
    const hasIN16Resource = session?.user?.employeeFirmCode === 'IN16';
    const hasPropelReportAccess = isAdmin || propelRm || (hasNoRole && hasIN16Resource && (isManager || isPartner));
    const isAdminOrRM = ['admin', 'resource_manager', 'guest_rm', 'resource_manager_lead'].some((role) => session?.user?.role?.includes(role));
    const isRecruiterOrHr = ['hrbp', 'recruiter', 'sub_recruiter'].some((role) => session?.user?.role?.includes(role)) && !isAdminOrRM;
    const isPartnerOrManager = ['partner', 'manager'].includes(session?.user?.gradeLevel) && !isAdminOrRM;
    const isResource = session?.user?.gradeLevel === 'resource' && !isPartnerOrManager && !isAdminOrRM;
    const isResourceDownloadHistoryPage = '/download-history' && session?.user?.gradeLevel === 'resource' && (isRecuritor || isSubRecuritor);
    const isServiceLineLead = session?.user?.role?.includes('service_line_lead');
    const isServiceCategoryLead = session?.user?.role?.includes('service_category_lead');
    const isPodLead = session?.user?.role?.includes('pod_lead');
    const isInTakeCoordinator = session?.user?.role?.includes('intake_coordinator');
    const isCECUser = isManager || isPartner || isServiceLineLead || isServiceCategoryLead || isPodLead || isInTakeCoordinator;
    const canViewCecReports = isCECUser || isAdminOrRmV2;
    const isTpcrRmOrRmLead = isTpcrRm || isTpcrRmLead;
    const isCECStructureTabEnabled = featureFlagService.isFeatureEnabled(CEC_STRUCTURE_TAB);
    const isCECStructureNonAdminEnabled = featureFlagService.isFeatureEnabled(CEC_STRUCTURE_TAB_NON_ADMIN);
    const isResourceAndPL = isResource && isPodLead;
    const isResourceAndSllOrScl = isResource && (isServiceLineLead || isServiceCategoryLead)
    const showCecOnlyHelpMenu = (isResourceAndPL || isResourceAndSllOrScl) && !isAdminOrPartnerOrManager;
    const shouldShowHelpMenu = isAdminOrPartnerOrManager || showCecOnlyHelpMenu;
    const getHelpType = () => {
        if (session?.user?.gradeLevel === 'partner') {
            return 'partner';
        }

        if (session?.user?.manager === 'manager') {
            return 'manager';
        }

        if (isRM) {
            return 'rm';
        }

        if (isAdmin) {
            return 'admin';
        }

        return 'employee';
    };

    const resourceMenuOptions = [
        {
            title: 'Home',
            isActive: isActive(`/resource-dashboard`),
            onClick: () => router.push('/resource-dashboard')
        },
        {
            title: 'My Profile',
            isActive: isActive(`/my-profile`),
            onClick: () => router.push(`/my-profile`)
        },
        {
            title: 'My Schedules',
            isActive: isActive('my-schedules'),
            onClick: () => router.push('/my-schedules')
        },
        ...(showTimeSheet ? [
            {
                title: 'Timesheet Recommendation',
                isActive: isActive('timesheet'),
                onClick: () => router.push('/timesheet')
            }
        ] : []),
        ...(isTpcrRmOrRmLead ? [] : [{
            title: (
                <RenderSubMenu
                    label='Opportunities'
                    menus={
                        getOpportunityMenuOptions({ isActive, setToastData, type: 'employee', router })
                    }
                />
            ),
            isActive: false,
            onClick: () => false
        }]),
        ...(hasAccessToFirmInitiative ? [{
            title: 'Firm Initiatives',
            isActive: isActive('firm-initiatives'),
            onClick: () => router.push('/firm-initiatives')
        }] : []),
        ...((isResource && isAuditUser && !isResourceDownloadHistoryPage) ? [
            {
                title: 'Stock/Cash Count',
                isActive: isActive('stock-count'),
                onClick: () => router.push('/stock-count')
            }
        ] : []),
        ...((isResource && isRecruiterOrHr) ? [
            {
                title: 'Requisitions',
                isActive: isActive('requisition'),
                onClick: () => router.push('/requisitions')
            }
        ] : []),
        ...(shouldShowHelpMenu ? [{
            title: (
                <RenderSubMenu
                    label='Help'
                    menus={
                        getHelpMenuOptions({ isActive, setToastData, type: 'employee', disableForTpcrRm: !isTpcrRmOrRmLead, showOnlyCecGuide: showCecOnlyHelpMenu })
                    }
                />
            ),
            isActive: false,
            onClick: () => false
        }] : [])
    ];

    const menuOptions = useMemo(() => ({
        home: {
            title: 'Home',
            isActive: pathname === '/',
            onClick: () => router.push('/')
        },
        project: {
            title: 'Projects',
            isActive: isActive('projects'),
            onClick: () => router.push('/projects')
        },
        demands: {
            title: 'Demands',
            isActive: isActive('demands'),
            onClick: () => router.push('/demands')
        },
        allocation: {
            title: 'Allocations',
            isActive: isActive('allocations'),
            onClick: () => router.push('/allocations')
        },
        requisition: {
            title: 'Requisitions',
            isActive: isActive('requisitions'),
            onClick: () => router.push('/requisitions')
        },
        internalGIG: {
            title: 'Internal Gig',
            isActive: isActive('internal-gig'),
            onClick: () => router.push('/internal-gig')
        },
        employees: {
            title: 'Employees',
            isActive: isActive('employees'),
            onClick: () => router.push('/employees')
        },
        reportees: {
            title: 'Reportees',
            isActive: isActive('reportee-list'),
            onClick: () => router.push('/reportee-list/reportee')
        },
        rmReview: {
            title: 'RM Review',
            isActive: isActive('actions'),
            onClick: () => router.push('/actions?gradeLevel=rm-review')
        },
        managerReview: {
            title: 'Manager Review',
            isActive: isActive('actions'),
            onClick: () => router.push('/actions?gradeLevel=manager-review')
        },
        partnerReview: {
            title: 'Partner Review',
            isActive: isActive('reviews'),
            onClick: () => router.push('/reviews')
        },
        availabilityReport: {
            title: 'Availability Reports',
            isActive: isActive('availability-report'),
            onClick: () => router.push('/availability-report')
        },
        statusReport: {
            title: 'Status Reports',
            isActive: isActive('status-report'),
            onClick: () => router.push('/status-report')
        },
        openRequirementReport: {
            title: 'Open requirement report',
            isActive: isActive('export-report'),
            onClick: () => router.push('/export-report?report=open-requirement')
        },
        stockCashCountAvailabilityReport: {
            title: 'Stock/Cash Count Availability Report',
            isActive: isActive('stock-cash-count-availability-report'),
            onClick: () => router.push('/stock-cash-count-availability-report')
        },
        propelReport: {
            title: 'Propel Report',
            isActive: isActive('propel-report'),
            onClick: () => router.push('/propel-report')
        },
        downloadReport: {
            title: 'Downloads',
            isActive: isActive('download-history'),
            onClick: () => router.push('/download-history')
        },
        stockCount: {
            title: 'Stock/Cash Count',
            isActive: isActive('stock-count'),
            onClick: () => router.push('/stock-count')
        },
        auditTrails: {
            title: 'Audit Trails',
            isActive: isActive('audit-trails'),
            onClick: () => router.push('/audit-trails')
        },
        firmInitiative: {
            title: 'Firm initiatives',
            isActive: isActive('firm-initiatives'),
            onClick: () => router.push('/firm-initiatives')
        },
        help: {
            title: (
                <RenderSubMenu
                    label='Help'
                    menus={
                        getHelpMenuOptions({ isActive, setToastData, type: getHelpType(), showFieldGlass: true, disableForTpcrRm: !isTpcrRmOrRmLead, showOnlyCecGuide: showCecOnlyHelpMenu })
                    }
                />
            ),
            isActive: false,
            onClick: () => false
        },
        ...(hideCecFeature ? {} : {
            cecAvailabilityReport: {
                title: 'CEC Availability Reports',
                isActive: isActive('cec-availability-report'),
                onClick: () => router.push('/cec-availability-report')
            },
            cecStatusReport: {
                title: 'CEC Status Reports',
                isActive: isActive('cec-status-report'),
                onClick: () => router.push('/cec-status-report')
            },
            workPackageReport: {
                title: 'Work Package Report',
                isActive: isActive('work-package-report') || isActive('work-package-report-pod-lead'),
                onClick: () => router.push(isPodLead ? '/work-package-report-pod-lead' : '/work-package-report')
            },
            resourceReport: {
                title: 'Resource Report',
                isActive: isActive('resource-report') || isActive('resource-report-pod-lead'),
                onClick: () => router.push(isPodLead ? '/resource-report-pod-lead' : '/resource-report'),
            },
            cecVarianceReport: {
                title: 'CEC Variance Report',
                isActive: isActive('cec-variance'),
                onClick: () => router.push('/cec-variance')
            }
        })
    }), [pathname, isAdminOrRm]);

    const cecMenuOptions = {
        intakeRequest: {
            title: 'Intake Requests',
            isActive: isActive('intake-request'),
            onClick: () => router.push('/intake-request')
        },
        workPackages: {
            title: 'Work Packages',
            isActive: isActive('work-packages'),
            onClick: () => router.push('/work-packages')
        },
        podWorkPackages: {
            title: 'Work Packages',
            isActive: isActive('work-packages'),
            onClick: () => router.push('/work-packages-pod-lead')
        },
        pods: {
            title: 'PODs',
            isActive: isActive('pods'),
            onClick: () => router.push('/pods')
        },
        assignPods: {
            title: 'PODs',
            isActive: isActive('pods'),
            onClick: () => router.push('/assigned-pods')
        },
        cecStructure: {
            title: 'CEC Structure',
            isActive: isActive('cec-structure'),
            onClick: () => router.push('/cec-structure')
        }
    }

    const cecMenu = {
        title: (
            <RenderSubMenu
                label='CEC'
                menus={(isDelegate && (delegatePaths.includes('/work-packages') || delegatePaths.includes('/assigned-pods'))) ? [
                    ...(isServiceLineLead || isServiceCategoryLead ? [cecMenuOptions.workPackages] : []),
                    ...(isServiceCategoryLead ? [cecMenuOptions.pods] : []),
                    ...(isPodLead ? [cecMenuOptions.podWorkPackages] : []),
                    ...(isPodLead ? [cecMenuOptions.assignPods] : []),
                ] : [
                    ...((isManager || isInTakeCoordinator || isPartner || isRM) ? [cecMenuOptions.intakeRequest] : []),
                    ...(isServiceLineLead || isServiceCategoryLead ? [cecMenuOptions.workPackages] : []),
                    ...(isPodLead ? [cecMenuOptions.podWorkPackages] : []),
                    ...(isServiceCategoryLead ? [cecMenuOptions.pods] : []),
                    ...(isPodLead ? [cecMenuOptions.assignPods] : []),
                    ...(!isAdmin && (isServiceCategoryLead || isPodLead || isServiceLineLead || isPartnerOrManager) && isCECStructureNonAdminEnabled ? [cecMenuOptions.cecStructure] : [])
                ]}
            />
        ),
        isActive: isActive('cec'),
        onClick: () => false
    };

    const reportsMenu = {
        title: (
            <RenderSubMenu
                label='Reports'
                menus={
                    [
                        ...(
                            isDelegate ? [
                                ...(delegatePaths.includes('/availability-report') && !isResourceAndPL ? [menuOptions.availabilityReport, menuOptions.cecAvailabilityReport] : [])
                            ]
                                : [
                                    ...(!isTpcrRmOrRmLead && !(isResourceAndPL || isResourceAndSllOrScl) ? [menuOptions.availabilityReport, menuOptions.cecAvailabilityReport] : []),
                                    ...((isResourceAndPL || isResourceAndSllOrScl) ? [menuOptions.cecAvailabilityReport] : []),
                                    // ...((isAdminOrRmV1 || isTpcrRmOrRmLead) ? [menuOptions.openRequirementReport] : []),
                                    ...(isAdminOrRmV1 && !(isResourceAndPL || isResourceAndSllOrScl) ? [menuOptions.statusReport, menuOptions.cecStatusReport] : []),
                                    ...((isResourceAndPL || isResourceAndSllOrScl) ? [menuOptions.cecStatusReport] : []),
                                    ...((hasStockCashReportAccess && !isTpcrRmOrRmLead) ? [menuOptions.stockCashCountAvailabilityReport] : []),
                                    ...((hasPropelReportAccess && !isTpcrRmOrRmLead) ? [menuOptions.propelReport] : []),
                                    ...(isAdminOrRmV1 || isBusinessTeam || isTpcrRmOrRmLead || isTalentAdvisor || isRecuritor || isSubRecuritor || ((isManager || isPartner) && (isAudit || session?.user?.isAuditRM)) ? [menuOptions.downloadReport] : [])
                                ]
                        ),
                        ...(canViewCecReports ? [menuOptions.workPackageReport] : []),
                        ...(canViewCecReports ? [menuOptions.resourceReport] : []),
                        ...(isDelegate ? [...(delegatePaths.includes('/propel-report') ? [menuOptions.propelReport, menuOptions.downloadReport] : [])] : []),
                        ...(canViewCecReports ? [menuOptions.cecVarianceReport] : [])
                    ].filter(Boolean)
                }
            />
        ),
        isActive: isActive('reports'),
        onClick: () => false
    };

    const reviewMenu = {
        title: (
            <RenderSubMenu
                label='Reviews'
                menus={[
                    ...(isAdminOrRm || isGuestRm || isRMLead || isTpcrRmOrRmLead || delegatePaths.includes('manager')
                        ? [menuOptions.rmReview]
                        : []),
                    ...(isManager || delegatePaths.includes('manager')
                        ? [menuOptions.managerReview]
                        : []),
                    ...(isDelegate
                        ? [...(delegatePaths.includes('/reviews') ? [menuOptions.partnerReview] : [])]
                        : [...(isPartner ? [menuOptions.partnerReview] : [])])
                ]}
            />
        ),
        isActive: isActive('actions') || isActive('reviews'),
        onClick: () => false
    };
    const projectsMenu = {
        title: (
            <RenderSubMenu
                label='Projects'
                menus={
                    [
                        ...((isPodLead && isResource) ? [] : [menuOptions.project]),
                        menuOptions.demands
                    ]
                }
            />
        ),
        isActive: isActive('projects'),
        onClick: () => false
    };

    const getReviewMenu = () => {
        if (isManager && !isPartner && !hasRmAccess && !isAdmin) return [menuOptions.managerReview];
        if (isPartner && !isManager && !hasRmAccess && !isAdmin) return [menuOptions.partnerReview];
        if (hasRmAccess && !isPartner && !isManager && !isAdmin) return [menuOptions.rmReview];
        return [reviewMenu];
    };

    const menus = useMemo(() => {
        if (isResourceDownloadHistoryPage) {
            return [...resourceMenuOptions]
        }
        return [
            menuOptions.home,
            ...(isDelegate ? [...(delegatePaths.includes('/projects') ? [projectsMenu] : [])] : [projectsMenu]),
            ...(isDelegate ? [...(delegatePaths.includes('/allocations') ? [menuOptions.allocation] : [])] : [menuOptions.allocation]),
            ...(isAdminOrRm ? [{
                title: (
                    <RenderSubMenu
                        label='Requisitions'
                        menus={[
                            menuOptions.requisition,
                            menuOptions.internalGIG
                        ]}
                    />
                ),
                onClick: () => false,
                isActive: (pathname === '/internal-gig-employee' || pathname.startsWith('/internal-gig-employee/')) ? false : isActive('requisitions') || isActive('internal-gig')
            }] : [
                ...(isDelegate ? [...(delegatePaths.includes('/requisitions') ? [menuOptions.requisition] : [])] : [menuOptions.requisition])
            ]),
            ...((isAdminOrRm || isGuestRm || isTalentAdvisor || isTpcrRmOrRmLead) ? [{
                title: (
                    <RenderSubMenu
                        label='Employees'
                        menus={
                            [
                                menuOptions.employees,
                                ...(isTalentAdvisor ? [] : [menuOptions.reportees])
                            ]
                        }
                    />
                ),
                onClick: () => false,
                isActive: isActive('employees')
            }] : [
                ...(isDelegate ? [...(delegatePaths.includes('/reportee-list/reportee') ? [menuOptions.reportees] : [])] : [menuOptions.reportees])
            ]),
            ...(isDelegate ? [...(delegatePaths.includes('/availability-report') || delegatePaths.includes('/propel-report') ? [reportsMenu] : [])] : [reportsMenu]),
            ...(!hideCecFeature && isCECUser && !isDelegate ? [cecMenu] : []),
            ...((!hideCecFeature && isCECUser && isDelegate && (delegatePaths.includes('/work-packages') || delegatePaths.includes('/assigned-pods'))) ? [cecMenu] : []),
            ...(isDelegate ? [...(delegatePaths.includes('/reviews') ? [reviewMenu] : [])] : isTpcrRmOrRmLead ? [] : getReviewMenu()),
            ...(session?.user?.role?.includes('admin') || session?.user?.role?.includes('super_admin') || session?.user?.role?.includes('resource_manager') || session?.user?.role?.includes('guest_rm') || isRMLead ? [{
                title: (
                    <RenderSubMenu
                        label='Admin'
                        menus={
                            (session?.user?.role?.includes('resource_manager') || session?.user?.role?.includes('guest_rm') || isRMLead) && !session?.user?.role?.includes('admin')
                                ? [
                                    {
                                        title: 'Roles and Permissions',
                                        isActive: pathname === '/admin/permissions',
                                        onClick: () => router.push('/admin/permissions')
                                    },
                                    {
                                        title: 'Requisition Approvers ',
                                        isActive: pathname === '/admin/requisition-approvers',
                                        onClick: () => router.push('/admin/requisition-approvers')
                                    }
                                ]
                                : [
                                    {
                                        title: 'Roles and Permissions',
                                        isActive: pathname === '/admin/permissions',
                                        onClick: () => router.push('/admin/permissions')
                                    },
                                    {
                                        title: 'Requisition Template',
                                        isActive: pathname === '/admin/requisition-template',
                                        onClick: () => router.push('/admin/requisition-template')
                                    },
                                    {
                                        title: 'Requisition Approvers ',
                                        isActive: pathname === '/admin/requisition-approvers',
                                        onClick: () => router.push('/admin/requisition-approvers')
                                    },
                                    ...(isCECStructureTabEnabled ? [{
                                        title: 'CEC',
                                        isActive: pathname.includes('/admin/cec'),
                                        onClick: () => router.push('/admin/cec')
                                    }] : [])
                                ]
                        }
                    />
                ),
                onClick: () => false
            }] : []),
            ...(isDelegate ? [] : [{
                title: (
                    <RenderSubMenu
                        label='Opportunities'
                        menus={
                            getOpportunityMenuOptions({ isActive, type: 'manager', setToastData, router })
                        }
                    />
                ),
                isActive: isActive('job-posting') || isActive('internal-gig-employee') || isActive('firm-initiatives-employee'),
                onClick: () => false
            }]),
            ...(!isDelegate && hasAccessToFirmInitiative ? [{
                title: 'Firm Initiatives',
                isActive: pathname === '/firm-initiatives-employee' ? false : isActive('firm-initiatives'),
                onClick: () => router.push('/firm-initiatives?manager=true')
            }] : []),
            ...(isAdmin || (isDelegate && isAuditUser && delegatePaths.includes('/stock-count'))
                || (!isDelegate && isAuditUser) ? [menuOptions.stockCount] : []),
            ...((isAdmin && isDelegate) || (isDelegate && delegatePaths.includes('/firm-initiatives'))
                ? [menuOptions.firmInitiative] : []),
            ...(isAdminOrRm ? [menuOptions.auditTrails] : []),
            menuOptions.help
        ]
    }, [isDelegate, session, delegatePaths, delegateRoles, pathname, menuOptions, isAuditUser, isResourceDownloadHistoryPage]);

    const filterMenuByAccessType = (menus) => {
        if (isTpcrRmOrRmLead) {
            return menus.filter((menuItem) => {
                const menuItemTitle = menuItem.title.props?.label || menuItem.title;
                return ['Home', 'Requisitions', 'Reports', 'RM Review', 'Employees', 'Reportees', 'Help'].includes(menuItemTitle)
            });
        }

        if (isResourceAndPL) {
            return menus.filter((menuItem) => {
                const menuItemTitle = menuItem.title.props?.label || menuItem.title;
                return ['Projects', 'Allocations', 'CEC', 'Reports', 'Help'].includes(menuItemTitle);
            });
        }

        if (isResourceAndSllOrScl) {
            return menus.filter((menuItem) => {
                const menuItemTitle = menuItem.title.props?.label || menuItem.title;
                return ['CEC', 'Reports', 'Help'].includes(menuItemTitle);
            });
        }

        if (isGuestRm && !isAdmin && accessType !== 'all') {
            const res = menus.filter((menuItem) => MENU_ITEMS(session)[accessType].includes(menuItem.title) || (menuItem?.title?.props?.label && MENU_ITEMS(session)[accessType].includes(menuItem.title.props.label)));
            return res;
        }
        if (isTalentAdvisor && session?.user?.gradeLevel == 'resource') {
            return menus.filter((menuItem) => menuItem.title === 'Requisitions' || menuItem.title.props?.label === 'Employees');
        }
        if (isBusinessTeam && session?.user?.gradeLevel == 'resource') {
            return menus.filter((menuItem) => {
                const menuItemTitle = menuItem.title.props?.label || menuItem.title;
                return ['Home', 'Projects', 'Demands', 'Allocations', 'Requisitions', 'Reports', 'Stock/Cash Count'].includes(menuItemTitle)
            });
        }

        return menus;
    }

    return (
        {
            color: Color.green,
            isInverse: false,
            navigationMenu: filterMenuByAccessType(menus),
            quickActions: [
                {
                    title: 'Notification',
                    icon: 'notification__l__stroke',
                    isActive: false,
                    ariaLabel: 'External Navigation',
                    click: () => setShowEdit('notification')
                }
            ],
            isResponsive: true,
            userProfileData: {
                user: {
                    username: session?.user?.name,
                    initials: getInitials(session?.user?.name ? session?.user?.name : ''),
                    userInfo: session?.user?.designation,
                    photoURL: session?.profilePicture
                },
                className: '',
                navigation: [
                    ...(!session?.isDelegate ? [{
                        isDisabled: false,
                        label: 'Profile',
                        value: 'Profile',
                        onSelectedItem: () => router.push('/profile')
                    },
                    {
                        isDisabled: false,
                        label: 'Skill glossary',
                        value: 'Skill glossary',
                        onSelectedItem: () => router.push('/rm-skill-glossary')
                    }
                    ] : []),
                    ...(!session?.isDelegate ? [{
                        label: 'Delegations',
                        onSelectedItem: () => setShowEdit('Delegates')
                    }] : []),
                    ...(isAdminOrRm ? [{
                        isDisabled: false,
                        label: 'Downloads',
                        value: 'Downloads',
                        onSelectedItem: () => router.push('/download-history')
                    }] : []),
                    // {
                    //     isDisabled: false,
                    //     label: 'Guest RMs',
                    //     value: 'Guest RMs',
                    //     onSelectedItem: () => setShowEdit('GuestRMView')
                    // },
                    ...(session?.impersonatingUserId ? [] : [
                        ...((isAdminOrRmV2 || isRMLead || isTpcrRmOrRmLead || (isGuestRm && loginOnBehalf)) ? [{
                            isDisabled: false,
                            label: 'Login on behalf',
                            value: 'Login on behalf',
                            onSelectedItem: () => setShowEdit('LoginBehalf')
                        }] : [])
                    ]),
                    ...(((isManager || isPartner || isServiceCategoryLead || isServiceLineLead || isPodLead) && !session?.isDelegate) ? [{
                        isDisabled: false,
                        label: 'Delegate activity',
                        value: 'Delegate activity',
                        onSelectedItem: () => setShowEdit('DelegateActivity')
                    }] : []),
                    {
                        isDisabled: false,
                        label: 'Logout',
                        value: 'Logout',
                        onSelectedItem: async () => {
                            // Clear all stored view preferences on logout
                            BrowserStorageService.clearViewPreferences();

                            await signOut({
                                redirect: true,
                                callbackUrl: process.env.NEXT_PUBLIC_NODE_ENV === 'development'
                                    ? '/login' : '/logout'
                            });
                            // removeAuth(null);
                        }
                    }
                ]
            },
            responsivePriority: ['logo', 'projectName', 'userProfile', 'links', 'navigation'],
            theme: {},
            delegateTypes,
            customLightLogo: '/images/sPInE-logo.svg',
            customDarkLogo: '/images/sPInE-logo.svg',
            customLogoWidth: '112px',
            customLogoHeight: '36px'
        }
    );
};

export default HeaderArguments;
